import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info);
    // Stale chunk after a new deploy → silently reload once.
    const msg = error.message || '';
    const stale =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('Importing a module script failed');
    if (stale && !sessionStorage.getItem('__myday_chunk_reload__')) {
      sessionStorage.setItem('__myday_chunk_reload__', '1');
      window.location.reload();
      return;
    }
    this.setState({ info });
  }

  render() {
    if (!this.state.error) return this.props.children;

    const err = this.state.error;
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0814', color: '#f5f0ff',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        padding: '40px 20px',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{
            display: 'inline-block',
            width: 44, height: 44, borderRadius: 14,
            background: 'linear-gradient(135deg, #FF3DA5, #A855F7, #38BDF8)',
            marginBottom: 24,
          }} />
          <h1 style={{ fontSize: 32, fontWeight: 800, margin: '0 0 8px' }}>
            Something broke in the UI.
          </h1>
          <p style={{ color: '#c4bcdc', marginBottom: 24 }}>
            This is shown by MyDay's error boundary — the crash below is the real issue.
            Screenshot this and we'll fix it.
          </p>

          <div style={{
            background: '#18132a', border: '1px solid #4a3e70',
            borderRadius: 16, padding: 20, marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, color: '#847aa0', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              Error
            </div>
            <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 14, color: '#ff9ec1', wordBreak: 'break-word' }}>
              {err.name}: {err.message}
            </div>
          </div>

          {this.state.info?.componentStack && (
            <div style={{
              background: '#18132a', border: '1px solid #322a4e',
              borderRadius: 16, padding: 20,
            }}>
              <div style={{ fontSize: 12, color: '#847aa0', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Component stack (the looping component is at the top)
              </div>
              <pre style={{
                fontSize: 11, color: '#a8a0c4', whiteSpace: 'pre-wrap',
                margin: 0, maxHeight: 420, overflow: 'auto',
              }}>{this.state.info.componentStack}</pre>
            </div>
          )}

          {err.stack && (
            <details style={{ marginTop: 12, background: '#18132a', border: '1px solid #322a4e', borderRadius: 16, padding: 20 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>JS stack trace</summary>
              <pre style={{
                fontSize: 11, color: '#a8a0c4', whiteSpace: 'pre-wrap',
                marginTop: 12, maxHeight: 320, overflow: 'auto',
              }}>{err.stack}</pre>
            </details>
          )}

          <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px', borderRadius: 12,
                background: 'linear-gradient(135deg, #FF3DA5, #A855F7, #38BDF8)',
                color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer',
              }}
            >Reload</button>
            <button
              onClick={() => {
                try {
                  Object.keys(localStorage).forEach((k) => {
                    if (k.startsWith('myday-') || k.startsWith('supabase.')) localStorage.removeItem(k);
                  });
                } catch {}
                window.location.href = '/';
              }}
              style={{
                padding: '10px 20px', borderRadius: 12,
                background: '#18132a', border: '1px solid #4a3e70',
                color: '#f5f0ff', fontWeight: 600, cursor: 'pointer',
              }}
            >Clear local state + go home</button>
          </div>
        </div>
      </div>
    );
  }
}
